import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Card,
    CardContent,
    CardActions,
    Button,
} from '@mui/material';
import { useTheme } from '@mui/styles';
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import useDataFetchFunction from 'hooks/useDataFetchFunction';
import axios from 'axios';
import { getPricingUrlConstruct } from 'api/hiredrateURLConstruct';
import { GradientCircularProgress } from 'layout/tests/HrExperiment';

const PricingCard = ({ onPlanSelect, onContactSales }) => {
    const theme = useTheme();
    const [locale, setLocale] = useState('US');
    const [billingCycle, setBillingCycle] = useState('Monthly');
    const [pricingResp, , pricingIsLoading, fetchPricingFunc] = useDataFetchFunction();

    // Auto-detect locale
    useEffect(() => {
        const detectLocale = () => {
            const lang = navigator.language || navigator.languages?.[0] || 'en-US';
            const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
            const isIndia = lang.toLowerCase().includes('en-in') ||
                lang.toLowerCase().includes('hi') ||
                tz.startsWith('Asia/Kolkata') ||
                tz.startsWith('Asia/Calcutta');
            setLocale(isIndia ? 'IN' : 'US');
        };
        detectLocale();
    }, []);

    // Fetch pricing data
    useEffect(() => {
        fetchPricingFunc({
            axiosInstance: axios,
            method: 'get',
            url: getPricingUrlConstruct(20),
            requestConfig: { headers: { 'Content-Type': 'application/json' } },
        });
    }, []);

    // Filter and sort plans
    let planHD = [];
    let enterprisePlan = null;
    if (pricingResp?.pricing_result) {
        const temp = pricingResp.pricing_result;
        temp.forEach((plan) => {
            if (plan.currency_code === (locale === 'IN' ? 'INR' : 'USD') &&
                plan.plan_cycle === billingCycle) {
                if (plan.name === 'Enterprise') enterprisePlan = plan;
                else planHD.push(plan);
            }
        });
    }
    planHD.sort((a, b) => a.display_price - b.display_price);
    if (enterprisePlan) planHD.push(enterprisePlan);

    return (
        <Box>
            {/* Filters */}
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 2 }}>
                <Box sx={{ minWidth: { xs: '100%', sm: 220 } }}>
                    <FormControl fullWidth>
                        <InputLabel id="locale-label">Locale</InputLabel>
                        <Select
                            labelId="locale-label"
                            value={locale}
                            label="Locale"
                            onChange={(e) => setLocale(e.target.value)}
                        >
                            <MenuItem value="US">US</MenuItem>
                            <MenuItem value="IN">INDIA</MenuItem>
                        </Select>
                    </FormControl>
                </Box>
                <Box sx={{ minWidth: { xs: '100%', sm: 220 } }}>
                    <FormControl fullWidth>
                        <InputLabel id="billing-label">Billing Cycle</InputLabel>
                        <Select
                            labelId="billing-label"
                            value={billingCycle}
                            label="Billing Cycle"
                            onChange={(e) => setBillingCycle(e.target.value)}
                        >
                            <MenuItem value="Monthly">Monthly</MenuItem>
                            <MenuItem value="Yearly">Yearly</MenuItem>
                        </Select>
                    </FormControl>
                </Box>
            </Box>

            {/* Cards */}
            {!pricingIsLoading ? (
                <Box sx={{ mt: 4 }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, justifyContent: 'flex-start' }}>
                        {planHD.length > 0 ? (
                            planHD.map((item, index) => {
                                const isEnterprise = item.name === 'Enterprise';
                                const isPopular = index === 1 && !isEnterprise;
                                return (
                                    <Box
                                        key={item.plan_unique_id}
                                        sx={{
                                            position: 'relative',
                                            width: { xs: '100%', sm: '340px' },
                                            display: 'flex',
                                            flexShrink: 0,
                                        }}
                                    >
                                        {isPopular && (
                                            <Box
                                                sx={{
                                                    position: 'absolute',
                                                    top: -14,
                                                    left: '50%',
                                                    transform: 'translateX(-50%)',
                                                    zIndex: 5,
                                                    px: 2,
                                                    py: 0.7,
                                                    borderRadius: '999px',
                                                    background: `linear-gradient(135deg, ${theme.palette.secondary.main}, ${theme.palette.secondary.dark})`,
                                                    color: '#fff',
                                                    fontSize: '0.75rem',
                                                    fontWeight: 700,
                                                    boxShadow: '0 8px 18px rgba(0,0,0,0.15)',
                                                }}
                                            >
                                                MOST POPULAR
                                            </Box>
                                        )}
                                        <Card
                                            variant="outlined"
                                            sx={{
                                                width: '100%',
                                                minHeight: '100%',
                                                borderRadius: '16px',
                                                overflow: 'hidden',
                                                border: isPopular
                                                    ? `2px solid ${theme.palette.primary.main}`
                                                    : `1px solid ${theme.palette.divider}`,
                                                background: theme.palette.background.paper,
                                                boxShadow: isPopular ? theme.shadows[6] : theme.shadows[1],
                                                display: 'flex',
                                                flexDirection: 'column',
                                                transition: 'all 0.2s ease',
                                                '&:hover': { boxShadow: theme.shadows[8] },
                                            }}
                                        >
                                            {/* Header */}
                                            <Box
                                                sx={{
                                                    position: 'relative',
                                                    overflow: 'hidden',
                                                    px: 4,
                                                    pt: 4,
                                                    pb: 4,
                                                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 55%, ${theme.palette.secondary.main} 100%)`,
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        position: 'absolute',
                                                        width: 220,
                                                        height: 220,
                                                        borderRadius: '50%',
                                                        background: 'rgba(255,255,255,0.08)',
                                                        top: -120,
                                                        right: -80,
                                                    }}
                                                />
                                                <Typography
                                                    sx={{
                                                        color: 'rgba(255,255,255,0.8)',
                                                        fontSize: '0.85rem',
                                                        fontWeight: 700,
                                                        letterSpacing: '0.08em',
                                                        textTransform: 'uppercase',
                                                        mb: 1,
                                                        position: 'relative',
                                                        zIndex: 2,
                                                    }}
                                                >
                                                    {isEnterprise ? 'Enterprise Solution' : 'Subscription Plan'}
                                                </Typography>
                                                <Typography
                                                    variant="h2"
                                                    sx={{ color: '#fff', fontWeight: 800, mb: 2, position: 'relative', zIndex: 2 }}
                                                >
                                                    {item.name}
                                                </Typography>
                                                {!isEnterprise ? (
                                                    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.5, position: 'relative', zIndex: 2 }}>
                                                        <Typography sx={{ fontSize: '3rem', fontWeight: 900, lineHeight: 1, color: '#fff' }}>
                                                            {item.currency_symbol}{item.display_price}
                                                        </Typography>
                                                        <Typography sx={{ color: 'rgba(255,255,255,0.85)', mb: 0.6, fontWeight: 500 }}>
                                                            /{billingCycle === 'Monthly' ? 'month' : 'year'}
                                                        </Typography>
                                                    </Box>
                                                ) : (
                                                    <Typography sx={{ color: 'rgba(255,255,255,0.92)', fontSize: '1rem', position: 'relative', zIndex: 2 }}>
                                                        Tailored pricing for organizations with advanced requirements.
                                                    </Typography>
                                                )}
                                            </Box>

                                            {/* Body */}
                                            <CardContent sx={{ px: 4, py: 4, display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                                                <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: theme.palette.text.secondary, letterSpacing: '0.08em', mb: 2 }}>
                                                    FEATURES INCLUDED
                                                </Typography>
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, flexGrow: 1 }}>
                                                    {!isEnterprise ? (
                                                        <>
                                                            {item.features.map((sub, idx) =>
                                                                sub.name.length > 0 && (
                                                                    <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, p: 1.5, borderRadius: '16px', background: theme.palette.grey[50], border: `1px solid ${theme.palette.grey[100]}` }}>
                                                                        <Box sx={{ width: 26, height: 26, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: theme.palette.primary.light, color: theme.palette.primary.dark, mt: 0.2 }}>
                                                                            <ArrowRightAltIcon sx={{ fontSize: 18 }} />
                                                                        </Box>
                                                                        <Typography sx={{ fontSize: '0.95rem', color: theme.palette.text.primary, lineHeight: 1.5 }}>
                                                                            {sub.name}
                                                                        </Typography>
                                                                    </Box>
                                                                )
                                                            )}
                                                            <Box sx={{ mt: 1, p: 2, borderRadius: '18px', background: theme.palette.secondary.light, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                                <Typography sx={{ fontWeight: 700, color: theme.palette.secondary.dark }}>Included Credits</Typography>
                                                                <Typography sx={{ fontWeight: 900, fontSize: '1.4rem', color: theme.palette.secondary.dark }}>{item.credits}</Typography>
                                                            </Box>
                                                        </>
                                                    ) : (
                                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                            {[
                                                                'Job Board & VMS Integrations',
                                                                'Custom Workflows & Automation',
                                                                'Advanced Security & Compliance',
                                                                'Private Cloud Deployments',
                                                                'Dedicated Success Manager',
                                                            ].map((feature, idx) => (
                                                                <Box key={idx} sx={{ display: 'flex', gap: 1.5, p: 1.5, borderRadius: '16px', background: theme.palette.grey[50], border: `1px solid ${theme.palette.grey[100]}` }}>
                                                                    <Box sx={{ width: 24, height: 24, borderRadius: '50%', background: theme.palette.primary.light, display: 'flex', alignItems: 'center', justifyContent: 'center', color: theme.palette.primary.dark, fontWeight: 800, fontSize: '0.75rem' }}>✓</Box>
                                                                    <Typography sx={{ color: theme.palette.text.primary, lineHeight: 1.5 }}>{feature}</Typography>
                                                                </Box>
                                                            ))}
                                                        </Box>
                                                    )}
                                                </Box>
                                            </CardContent>

                                            {/* Footer */}
                                            <CardActions sx={{ px: 4, pb: 4, pt: 0 }}>
                                                {!isEnterprise ? (
                                                    <Button
                                                        fullWidth
                                                        onClick={() => onPlanSelect(item)}
                                                        sx={{
                                                            height: 56,
                                                            borderRadius: '16px',
                                                            textTransform: 'none',
                                                            fontSize: '1rem',
                                                            fontWeight: 700,
                                                            color: '#fff',
                                                            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                                                            boxShadow: '0 10px 24px rgba(0,0,0,0.12)',
                                                            '&:hover': {
                                                                background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                                                                transform: 'translateY(-2px)',
                                                            },
                                                        }}
                                                    >
                                                        Choose Plan
                                                    </Button>
                                                ) : (
                                                    <Button
                                                        fullWidth
                                                        onClick={onContactSales}
                                                        sx={{
                                                            height: 56,
                                                            borderRadius: '16px',
                                                            textTransform: 'none',
                                                            fontSize: '1rem',
                                                            fontWeight: 700,
                                                            border: `2px solid ${theme.palette.primary.main}`,
                                                            color: theme.palette.primary.main,
                                                            '&:hover': { background: theme.palette.primary.light },
                                                        }}
                                                    >
                                                        Contact Sales
                                                    </Button>
                                                )}
                                            </CardActions>
                                        </Card>
                                    </Box>
                                )
                            })
                        ) : (
                            <Typography>No plans available</Typography>
                        )}
                    </Box>
                </Box>
            ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: '50vh', padding: '20px' }}>
                    <GradientCircularProgress />
                    <Typography variant="caption" mt={2} sx={{ fontSize: '1.8rem', fontWeight: 'bold' }}>Loading Plans...</Typography>
                </Box>
            )}
        </Box>
    );
};

export default PricingCard;